import React, { useEffect, useRef, useState } from 'react';
import { warnLog } from '@/utils/logger';

interface GoogleLoginButtonProps {
  onSuccess: (credential: string) => void;
  onError: (error: any) => void;
  disabled?: boolean;
}

declare global {
  interface Window {
    google?: any;
  }
}

const GoogleSvg = () => (
  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
    <path
      fill="currentColor"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="currentColor"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="currentColor"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="currentColor"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const GoogleLoginButton: React.FC<GoogleLoginButtonProps> = ({
  onSuccess,
  onError,
  disabled = false,
}) => {
  const buttonRef = useRef<HTMLDivElement>(null);
  const [renderState, setRenderState] = useState<'loading' | 'rendered' | 'failed'>('loading');

  const handleRedirectLogin = () => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId) {
      onError('Google Client ID not configured');
      return;
    }
    const nonce = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);
    sessionStorage.setItem('google_oauth_nonce', nonce);
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: window.location.origin + '/auth/callback',
      response_type: 'id_token',
      scope: 'openid email profile',
      nonce,
    });
    window.location.href = 'https://accounts.google.com/o/oauth2/v2/auth?' + params.toString();
  };

  useEffect(() => {
    if (disabled) return;

    let checkTimer: ReturnType<typeof setTimeout>;

    const loadGoogleScript = () => {
      if (window.google) {
        initializeGoogleSignIn();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initializeGoogleSignIn;
      script.onerror = () => setRenderState('failed');
      document.head.appendChild(script);
    };

    const initializeGoogleSignIn = () => {
      const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

      if (!clientId) {
        warnLog('Google Client ID not configured - Google login disabled');
        setRenderState('failed');
        return;
      }

      if (window.google && buttonRef.current) {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleGoogleResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
        });

        window.google.accounts.id.renderButton(buttonRef.current, {
          theme: 'filled_black',
          size: 'large',
          text: 'signin_with',
          width: 320,
          logo_alignment: 'left',
        });

        // Check after 1.5s if the iframe actually rendered
        checkTimer = setTimeout(() => {
          if (buttonRef.current && buttonRef.current.childElementCount > 0) {
            setRenderState('rendered');
          } else {
            setRenderState('failed');
          }
        }, 1500);
      } else {
        setRenderState('failed');
      }
    };

    const handleGoogleResponse = (response: any) => {
      if (response.credential) {
        onSuccess(response.credential);
      } else {
        onError('No credential received from Google');
      }
    };

    loadGoogleScript();

    return () => {
      clearTimeout(checkTimer);
      const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [onSuccess, onError, disabled]);

  if (disabled) {
    return (
      <div className="w-full">
        <button
          type="button"
          disabled
          className="w-full flex items-center justify-center px-4 py-2 border rounded-md shadow-sm text-sm font-medium cursor-not-allowed bg-gray-800 border-gray-600 text-gray-500"
        >
          <GoogleSvg />
          Sign in with Google
        </button>
      </div>
    );
  }

  if (renderState === 'failed') {
    return (
      <div className="w-full">
        <button
          type="button"
          onClick={handleRedirectLogin}
          className="w-full flex items-center justify-center px-4 py-2 border rounded-md shadow-sm text-sm font-medium bg-white border-gray-300 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <GoogleSvg />
          Sign in with Google
        </button>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div ref={buttonRef} className="w-full" />
    </div>
  );
};

export default GoogleLoginButton;
